import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { CheckCircleIcon, GlassesIcon } from 'lucide-react';
export function VirtualTryOn() {
  const features = ['Browse hundreds of designer frames', 'See how they look on your face in real-time', 'Save favorites and bring your picks to your appointment'];
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: 'easeOut'
      }
    }
  };
  return <section className="py-20 md:py-28 bg-brand-dark overflow-hidden relative" data-id="element-691">
      {/* Background decorative elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" data-id="element-692">
        <div className="absolute top-[-10%] right-[-5%] w-96 h-96 bg-brand-gold/5 rounded-full blur-3xl" data-id="element-693" />
        <div className="absolute bottom-[-10%] left-[-5%] w-80 h-80 bg-white/5 rounded-full blur-3xl" data-id="element-694" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-695">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-20 items-center" data-id="element-696">
          {/* Left Column: Content */}
          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} className="max-w-xl mx-auto lg:mx-0" data-id="element-697">
            <motion.h2 variants={itemVariants} className="text-section-mobile md:text-section text-white font-heading font-bold mb-6 leading-tight" data-id="element-698">
              Try On Glasses Virtually
            </motion.h2>

            <motion.p variants={itemVariants} className="text-white/70 text-lg mb-8 leading-relaxed" data-id="element-699">
              See how frames look on you before visiting our clinic. Our virtual
              try-on tool lets you browse and preview hundreds of frames from
              the comfort of your home.
            </motion.p>

            <motion.ul variants={itemVariants} className="space-y-4 mb-10" data-id="element-700">
              {features.map((feature, index) => <li key={index} className="flex items-start gap-3" data-id="element-701">
                  <CheckCircleIcon className="text-brand-gold w-6 h-6 shrink-0 mt-0.5" data-id="element-702" />
                  <span className="text-white/80 text-base" data-id="element-703">{feature}</span>
                </li>)}
            </motion.ul>

            <motion.div variants={itemVariants} data-id="element-704">
              <a href="https://preview.4patientcare.com/" target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 bg-brand-gold text-white px-8 py-4 rounded-button font-heading font-semibold text-lg hover:bg-brand-gold-light transition-colors shadow-lg focus-ring" data-id="element-705">
                <GlassesIcon className="w-5 h-5" data-id="element-706" />
                Try On Frames Now
              </a>
            </motion.div>
          </motion.div>

          {/* Right Column: Visual Mockup */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7,
          ease: 'easeOut'
        }} className="relative flex justify-center items-center" data-id="element-707">
            {/* Floating decorative dots */}
            <motion.div animate={{
            y: [0, -15, 0],
            opacity: [0.5, 1, 0.5]
          }} transition={{
            repeat: Infinity,
            duration: 3,
            ease: 'easeInOut'
          }} className="absolute top-10 left-10 md:left-20 w-4 h-4 bg-brand-gold rounded-full blur-[1px]" data-id="element-708" />
            <motion.div animate={{
            y: [0, 15, 0],
            opacity: [0.3, 0.8, 0.3]
          }} transition={{
            repeat: Infinity,
            duration: 4,
            ease: 'easeInOut',
            delay: 1
          }} className="absolute bottom-20 right-10 md:right-20 w-6 h-6 bg-brand-gold-light rounded-full blur-[2px]" data-id="element-709" />
            <motion.div animate={{
            scale: [1, 1.2, 1],
            opacity: [0.2, 0.5, 0.2]
          }} transition={{
            repeat: Infinity,
            duration: 5,
            ease: 'easeInOut',
            delay: 2
          }} className="absolute top-1/2 right-4 w-3 h-3 bg-white rounded-full blur-[1px]" data-id="element-710" />

            {/* Phone Mockup */}
            <motion.div animate={{
            y: [0, -10, 0]
          }} transition={{
            repeat: Infinity,
            duration: 5,
            ease: 'easeInOut'
          }} className="bg-white/10 backdrop-blur-md border border-white/20 rounded-[2.5rem] p-3 aspect-[9/16] w-full max-w-[280px] shadow-2xl relative z-10" data-id="element-711">
              {/* Phone Screen */}
              <div className="bg-gradient-to-b from-brand-dark to-brand-dark/80 rounded-[2rem] w-full h-full overflow-hidden relative flex flex-col items-center justify-center border border-white/5" data-id="element-712">
                {/* Top Notch */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/3 h-5 bg-white/10 rounded-b-xl backdrop-blur-sm" data-id="element-713" />

                {/* Screen Content */}
                <div className="flex flex-col items-center justify-center p-6 text-center z-10" data-id="element-714">
                  <motion.div animate={{
                  scale: [1, 1.05, 1]
                }} transition={{
                  repeat: Infinity,
                  duration: 3,
                  ease: 'easeInOut'
                }} className="w-24 h-24 bg-brand-gold/20 rounded-full flex items-center justify-center mb-6 border border-brand-gold/30 backdrop-blur-sm" data-id="element-715">
                    <GlassesIcon className="w-12 h-12 text-brand-gold" data-id="element-716" />
                  </motion.div>

                  <div className="w-16 h-1 bg-white/20 rounded-full mb-4" data-id="element-717" />
                  <h3 className="text-white font-heading font-bold text-xl mb-2" data-id="element-718">
                    Virtual Try-On
                  </h3>
                  <p className="text-white/50 text-xs" data-id="element-719">
                    Position your face in the frame
                  </p>

                  {/* Faux face frame scanner */}
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-20" data-id="element-720">
                    <div className="w-48 h-64 border-2 border-dashed border-brand-gold rounded-[3rem]" data-id="element-721" />
                  </div>
                </div>

                {/* Bottom Bar */}
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-1/3 h-1 bg-white/30 rounded-full" data-id="element-722" />
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>;
}